import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";

export default function SubadminList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showInactive, setShowInactive] = useState(false); // ✅ new

  const baseURL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("profileToken");
  const headers = { "x-access-token": token || "" };

  const fetchList = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(`${baseURL}/subadmin/list`, { headers });
      if (res.data?.status) setRows(res.data.result || []);
      else setError(res.data?.message || "Failed to fetch subadmins");
    } catch (err) {
      setError("Server error while fetching subadmins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line
  }, []);

  // ✅ counts
  const activeCount = useMemo(
    () => rows.filter((x) => Number(x.status) === 1).length,
    [rows]
  );
  const inactiveCount = useMemo(
    () => rows.filter((x) => Number(x.status) === 0).length,
    [rows]
  );

  // ✅ filtered list to render
  const visibleRows = useMemo(() => {
    return rows.filter((x) =>
      showInactive ? Number(x.status) === 0 : Number(x.status) === 1
    );
  }, [rows, showInactive]);

  const toggleStatus = async (id, current) => {
    const newStatus = Number(current) === 1 ? 0 : 1;

    try {
      const res = await axios.put(
        `${baseURL}/subadmin/status/${id}`,
        { status: newStatus },
        { headers }
      );

      if (res.data?.status) {
        setRows((p) => p.map((x) => (x.id === id ? { ...x, status: newStatus } : x)));

        // ✅ switch view automatically
        setShowInactive(newStatus === 0);

        Swal.fire("Success", "Status updated", "success");
      } else {
        Swal.fire("Error", res.data?.message || "Failed", "error");
      }
    } catch {
      Swal.fire("Error", "Failed to update status", "error");
    }
  };



  const handleDelete = async (id, username) => {
    const result = await Swal.fire({
      title: `Delete ${username}?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await axios.delete(`${baseURL}/subadmin/delete/${id}`, { headers });
      if (res.data?.status) {
        setRows((p) => p.filter((x) => x.id !== id));
        Swal.fire("Deleted!", "Subadmin deleted.", "success");
      } else {
        Swal.fire("Error", res.data?.message || "Failed", "error");
      }
    } catch {
      Swal.fire("Error", "Failed to delete", "error");
    }
  };

  if (loading)
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-success" role="status"></div>
        <p className="mt-3">Loading subadmins...</p>
      </div>
    );

  if (error) return <div className="text-center text-danger my-5"><p>{error}</p></div>;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0 text-success">
          {showInactive ? "Inactive Subadmins" : "Subadmins List"}{" "}
          <span className="badge bg-secondary ms-2">
            {showInactive ? inactiveCount : activeCount}
          </span>
        </h3>

        <div className="d-flex gap-2">
          {/* ✅ Toggle Active/Inactive */}
          <button
            className={`btn ${showInactive ? "btn-outline-success" : "btn-outline-danger"} rounded-pill px-4`}
            onClick={() => setShowInactive((p) => !p)}
            title={showInactive ? "Show Active Subadmins" : "Show Inactive Subadmins"}
          >
            {showInactive
              ? `Active Subadmins (${activeCount})`
              : `Inactive Subadmins (${inactiveCount})`}
          </button>

          <Link className="btn btn-success rounded-pill px-4" to="/add-subadmin">
            <i className="fas fa-plus me-2"></i> Add Subadmin
          </Link>
        </div>
      </div>

      {visibleRows.length === 0 ? (
        <p className="text-muted">
          {showInactive ? "No inactive subadmins available." : "No active subadmins available."}
        </p>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-bordered align-middle text-center">
            <thead className="table-success">
              <tr>
                <th>#</th>
                <th>Username</th>
                <th>Email</th>
                <th>Fullname</th>
                <th>Mobile</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {visibleRows.map((x, i) => (
                <tr
                  key={x.id}
                  className={Number(x.status) === 0 ? "table-secondary" : ""}
                  style={Number(x.status) === 0 ? { opacity: 0.9 } : {}}
                >
                  <td>{i + 1}</td>
                  <td>
                    {x.username}
                    {Number(x.status) === 0 && (
                      <span className="badge bg-danger ms-2">Inactive</span>
                    )}
                  </td>
                  <td>{x.email || "-"}</td>
                  <td>{x.fullname || "-"}</td>
                  <td>{x.mobile_no || "-"}</td>

                  <td>
                    <div className="form-check form-switch d-inline-block">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={Number(x.status) === 1}
                        onChange={() => toggleStatus(x.id, x.status)}
                      />
                      <label className="form-check-label">
                        {Number(x.status) === 1 ? "Active" : "Inactive"}
                      </label>
                    </div>
                  </td>

                  <td>
                    <div className="d-flex justify-content-center gap-3">
                      <i
                        className="fas fa-trash text-danger"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDelete(x.id, x.username)}
                        title="Delete"
                      ></i>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>
      )}
    </div>
  );
}
